import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const DetailedParameters = () => {
  const { id: deviceId, inverterId } = useParams();
  const navigate = useNavigate();
  
  // Collapsible states
  const [showPV, setShowPV] = useState(false);
  const [showAC, setShowAC] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showLoad, setShowLoad] = useState(false);

  // Mock data
  const pvData = Array.from({ length: 11 }, (_, i) => ({
    name: i === 0 ? 'PV' : `PV${i}`,
    voltage: i === 0 ? '--' : '0',
    current: i === 0 ? '--' : '0',
    power: '0'
  }));

  const acData = [
    { phase: 'R', voltage: '231.7', current: '0', power: '0', frequency: '50.0' },
    { phase: 'S', voltage: '231.8', current: '0', power: '0', frequency: '50.0' },
    { phase: 'T', voltage: '231.5', current: '0', power: '0', frequency: '50.0' }
  ];

  const gridData = {
    status: '--',
    dailyFeedIn: '0',
    totalFeedIn: '0',
    dailyPurchased: '0',
    totalPurchased: '0',
    feedInPower: '0',
    purchasedPower: '0'
  };

  const loadData = {
    dailyConsumption: '0',
    totalConsumption: '0',
    totalPower: '0',
    gridPower: '0',
    epsPower: '--'
  };

  const now = new Date();
  const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  // Collapsible section component - styled like the Detailed Parameters button
  const CollapsibleSection = ({ title, isOpen, onToggle, children }) => (
    <div className="mb-4 overflow-hidden">
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 rounded-2xl"
        style={{
          backgroundColor: '#1A2433',
          boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.3)'
        }}
      >
        <span className="font-semibold text-white text-lg">{title}</span>
        <svg 
          className={`w-6 h-6 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          style={{ color: '#A3AAB8' }}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-5 py-4 mt-2 rounded-2xl" style={{ backgroundColor: '#1A2433' }}>
          {children}
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ backgroundColor: '#0f1829' }}>
        {/* Header */}
        <div className="flex-shrink-0 p-4 flex items-center gap-4" style={{ backgroundColor: '#0f1829' }}>
          <button 
            onClick={() => navigate(`/devices/${deviceId}/inverter/${inverterId}`)}
            className="flex items-center gap-1 text-blue-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-white font-semibold text-lg flex-1 text-center pr-12">Detailed Parameters</h1>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4" style={{ backgroundColor: '#0f1829' }}>
          {/* PV Information */}
          <CollapsibleSection title="PV Information" isOpen={showPV} onToggle={() => setShowPV(!showPV)}>
            {/* Table header */}
            <div className="grid grid-cols-4 gap-2 mb-2 text-slate-400 text-sm">
              <div></div>
              <div className="text-center">Voltage(V)</div>
              <div className="text-center">Current(A)</div>
              <div className="text-center">Power(kW)</div>
            </div>
            {/* Table rows */}
            {pvData.map((pv, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 py-2 text-white text-sm">
                <div className="font-medium">{pv.name}</div>
                <div className="text-center">{pv.voltage}</div>
                <div className="text-center">{pv.current}</div>
                <div className="text-center">{pv.power}</div>
              </div>
            ))}
          </CollapsibleSection>

          {/* AC Information */}
          <CollapsibleSection title="AC Information" isOpen={showAC} onToggle={() => setShowAC(!showAC)}>
            {/* Table header */}
            <div className="grid grid-cols-5 gap-2 mb-2 text-slate-400 text-sm">
              <div></div>
              <div className="text-center">Voltage(V)</div>
              <div className="text-center">Current(A)</div>
              <div className="text-center">Power(kW)</div>
              <div className="text-center">Frequency(H...</div>
            </div>
            {/* Table rows */}
            {acData.map((ac, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 py-2 text-white text-sm">
                <div className="font-medium">{ac.phase}</div>
                <div className="text-center">{ac.voltage}</div>
                <div className="text-center">{ac.current}</div>
                <div className="text-center">{ac.power}</div>
                <div className="text-center">{ac.frequency}</div>
              </div>
            ))}
          </CollapsibleSection>

          {/* Grid Information */}
          <CollapsibleSection title="Grid Information" isOpen={showGrid} onToggle={() => setShowGrid(!showGrid)}>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Grid Status</span>
                <span className="text-white">{gridData.status}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Daily feed-in energy</span>
                <span className="text-white">{gridData.dailyFeedIn}kWh</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Total feed-in energy</span>
                <span className="text-white">{gridData.totalFeedIn}kWh</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Daily purchased energy</span>
                <span className="text-white">{gridData.dailyPurchased}kWh</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Total purchased energy</span>
                <span className="text-white">{gridData.totalPurchased}kWh</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Feed-in power</span>
                <span className="text-white">{gridData.feedInPower}kW</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Purchased power</span>
                <span className="text-white">{gridData.purchasedPower}kW</span>
              </div>
            </div>
          </CollapsibleSection>

          {/* Load Information */}
          <CollapsibleSection title="Load Information" isOpen={showLoad} onToggle={() => setShowLoad(!showLoad)}>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Daily Load Consumption</span>
                <span className="text-white">{loadData.dailyConsumption}kWh</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Total Load Consumption</span>
                <span className="text-white">{loadData.totalConsumption}kWh</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Total Load Power</span>
                <span className="text-white">{loadData.totalPower}kW</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">Grid Load Power</span>
                <span className="text-white">{loadData.gridPower}kW</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white font-medium">EPS Load Power</span>
                <span className="text-white">{loadData.epsPower}kW</span>
              </div>
            </div>
          </CollapsibleSection>

          {/* Timestamp */}
          <div className="text-center text-slate-500 text-sm mt-4">
            {timestamp}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DetailedParameters;
